import { useCallback, useRef, useState } from 'react';
import type { BcmNode } from '../api/bcmApi';

export interface BcmBreadcrumbItem {
  id: string;
  name: string;
}

export interface BcmNavState {
  currentParentIds: string[];
  breadcrumb: BcmBreadcrumbItem[];
}

export interface BcmNavigationApi {
  navState: BcmNavState;
  initRoots: (rootBcIds: string[] | null, allNodes: BcmNode[]) => void;
  drillDown: (node: BcmNode) => void;
  navigateTo: (item: BcmBreadcrumbItem) => void;
  goBack: () => void;
  reset: () => void;
}

export function useBcmNavigation(): BcmNavigationApi {
  const [navState, setNavState] = useState<BcmNavState>({
    currentParentIds: [],
    breadcrumb: [],
  });
  const rootParentIdsRef = useRef<string[]>([]);

  const initRoots = useCallback((rootBcIds: string[] | null, allNodes: BcmNode[]) => {
    if (rootBcIds && rootBcIds.length > 0) {
      rootParentIdsRef.current = rootBcIds;
      setNavState({ currentParentIds: rootBcIds, breadcrumb: [] });
      return;
    }

    const allBcIds = new Set(allNodes.map((node) => node.bc_entry_id));
    const rootNodes = allNodes.filter(
      (node) => !node.entry?.parent_id || !allBcIds.has(node.entry.parent_id),
    );

    const detectedRootIds = rootNodes.map((node) => node.bc_entry_id);
    rootParentIdsRef.current = detectedRootIds;
    setNavState({ currentParentIds: detectedRootIds, breadcrumb: [] });
  }, []);

  const drillDown = useCallback((node: BcmNode) => {
    const item = {
      id: node.bc_entry_id,
      name: node.entry?.name ?? node.bc_entry_id,
    };
    setNavState((prev) => ({
      currentParentIds: [node.bc_entry_id],
      breadcrumb: [...prev.breadcrumb, item],
    }));
  }, []);

  const navigateTo = useCallback((item: BcmBreadcrumbItem) => {
    setNavState((prev) => {
      const idx = prev.breadcrumb.findIndex((crumb) => crumb.id === item.id);
      if (idx === -1) return prev;
      return {
        currentParentIds: [item.id],
        breadcrumb: prev.breadcrumb.slice(0, idx),
      };
    });
  }, []);

  const goBack = useCallback(() => {
    setNavState((prev) => {
      if (prev.breadcrumb.length <= 1) {
        return { currentParentIds: rootParentIdsRef.current, breadcrumb: [] };
      }
      const parent = prev.breadcrumb[prev.breadcrumb.length - 2];
      return {
        currentParentIds: [parent.id],
        breadcrumb: prev.breadcrumb.slice(0, -1),
      };
    });
  }, []);

  const reset = useCallback(() => {
    setNavState({ currentParentIds: rootParentIdsRef.current, breadcrumb: [] });
  }, []);

  return { navState, initRoots, drillDown, navigateTo, goBack, reset };
}
